/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {ActionButton, Spinner} from 'screens/commons';

import {ProgressBar} from 'components/ProgressBar';

const propTypes = {
  onChangeHandler: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isRedirectLoading: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isIfscValid: PropTypes.bool.isRequired,
  ifscResponse: PropTypes.object.isRequired,
  skipStep: PropTypes.func.isRequired,
  showSkipButton: PropTypes.object.isRequired,
};

const PartnerBankDetails = ({
  onChangeHandler,
  onSubmit,
  formData,
  isLoading,
  isRedirectLoading,
  isDisabled,
  isIfscValid,
  ifscResponse,
  skipStep,
  showSkipButton,
}) =>
  isRedirectLoading ? (
    <LoaderContainer>
      <ProgressBar
        heading="We are fetching next steps"
        subheading=""
        waitTimeInSec={100}
        loadingTexts={[]}
        showShadow={false}
      />
    </LoaderContainer>
  ) : (
    <PartnerBankDetailsWrapper>
      {showSkipButton ? <SkipButton onClick={skipStep}>Skip</SkipButton> : null}
      <Container>
        <CardContainer>
          <CardContent>
            <FormSection>
              <FormField>
                <FieldLabel>Account Number</FieldLabel>
                <InputField>
                  <Input
                    name="beneficiaryAccountNo"
                    type="number"
                    value={formData.beneficiaryAccountNo}
                    onChange={onChangeHandler}
                  />
                </InputField>
              </FormField>

              <FormField>
                <FieldLabel>IFSC Code</FieldLabel>
                <InputField>
                  <Input
                    name="beneficiaryAccountIfsc"
                    value={formData.beneficiaryAccountIfsc}
                    onChange={onChangeHandler}
                  />
                  {isIfscValid && ifscResponse?.bank ? <VerifiedBadge>Valid</VerifiedBadge> : null}
                </InputField>
                {!isIfscValid ? (
                  <ErrorMessage>Invalid IFSC Code, please check.</ErrorMessage>
                ) : ifscResponse?.address ? (
                  <FormField className="mt-3">
                    <FieldLabel>Bank Details</FieldLabel>
                    <BankDetailsContainer>
                      {ifscResponse?.bank && (
                        <BankDetailRow>
                          <BankDetailLabel>Bank</BankDetailLabel>
                          <BankDetailValue>{ifscResponse?.bank}</BankDetailValue>
                        </BankDetailRow>
                      )}
                      {ifscResponse?.branch && (
                        <BankDetailRow>
                          <BankDetailLabel>Branch</BankDetailLabel>
                          <BankDetailValue>{ifscResponse?.branch}</BankDetailValue>
                        </BankDetailRow>
                      )}
                      {ifscResponse?.city && (
                        <BankDetailRow>
                          <BankDetailLabel>City</BankDetailLabel>
                          <BankDetailValue>
                            {ifscResponse?.city}
                            {ifscResponse?.state ? `, ${ifscResponse?.state}` : ''}
                          </BankDetailValue>
                        </BankDetailRow>
                      )}
                      {ifscResponse?.address && (
                        <BankDetailRow>
                          <BankDetailLabel>Address</BankDetailLabel>
                          <BankDetailValue className="address-value">
                            {ifscResponse?.address}
                          </BankDetailValue>
                        </BankDetailRow>
                      )}
                    </BankDetailsContainer>
                  </FormField>
                ) : null}
              </FormField>
            </FormSection>

            <CardFooter>
              <ActionButton
                isDisabled={isDisabled}
                buttonText="Save"
                callback={onSubmit}
                isLoading={isLoading}
              />
            </CardFooter>
          </CardContent>
        </CardContainer>
      </Container>
    </PartnerBankDetailsWrapper>
  );

const PartnerBankDetailsWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.background.primary};
`;

const Container = styled.div`
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 1.5rem;
`;

const CardContainer = styled.div`
  overflow: hidden;
`;

const CardContent = styled.div`
  padding: 0 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;
const SkipButton = styled.span`
  color: ${(props) => props.theme.colors.brand.primary};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
`;
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const InputField = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.border?.default || '#DDDDDD'};
  border-radius: ${(props) => props.theme.borderRadius?.md || '6px'};
  padding: 0.75rem;
`;

const Input = styled.input.attrs({
  className: 'body-regular',
})`
  border: none;
  background: transparent;
  outline: none;
  color: ${(props) => props.theme.colors.text.primary};
  width: 100%;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const VerifiedBadge = styled.span`
  background: ${(props) => props.theme.colors.feedback.successLight || '#dcfce7'};
  color: ${(props) => props.theme.colors.feedback.success || '#15803d'};
  padding: 0.25rem 0.5rem;
  border-radius: ${(props) => props.theme.borderRadius?.sm || '4px'};
  font-size: 0.75rem;
  margin-left: 0.75rem;
`;

const ErrorMessage = styled.p`
  color: ${(props) => props.theme.colors.feedback.error};
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

const BankDetailsContainer = styled.div`
  background: ${(props) => props.theme.colors.background.secondary || '#f8fafc'};
  border: 1px solid ${(props) => props.theme.colors.border?.default || '#DDDDDD'};
  border-radius: ${(props) => props.theme.borderRadius?.md || '6px'};
  padding: 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.text.secondary};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BankDetailRow = styled.div`
  display: flex;
  padding: 0.5rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.border?.light || '#F5F5F5'};

  &:last-child {
    border-bottom: none;
  }
`;

const BankDetailLabel = styled.span`
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.secondary};
  flex: 0 0 80px;
`;

const BankDetailValue = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-weight: ${(props) => props.theme.typography.fontWeight.regular};
  flex: 1;

  &.address-value {
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
    white-space: pre-wrap;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

PartnerBankDetails.propTypes = propTypes;

export default PartnerBankDetails;
